import React from 'react'
import { Button, Icon, IconButton, Menu, MenuItem, Paper, Snackbar, Table, TableBody, TableCell, TableContainer, TableHead, TablePagination, TableRow, TableSortLabel } from '@material-ui/core'
import { ConfirmDialog, ModuleForm, Search, FormDialog, FixedLoadingIndicator, LabelDisplayedRows, ServerErrorHandler } from 'components'
import { useInputValue, useTableModel } from 'hooks'
import { MODULE_PATH, NEW_PATH, AUTHENTICATION_PATH, MODULE_TABLE_COLUMNS, EDIT_PATH, deleteModuleService, SMALL_ROWS_PER_PAGINATION_OPTIONS, MODULE_VIEW_PATH, ROOT_TITLE, MODULE_TITLE } from 'lib'
import { useHistory, useLocation } from 'react-router'
import { IMessageConfig, IModule } from 'types'
import { ManagementContext } from 'context'
import { Alert } from '@material-ui/lab'
import { Helmet } from 'react-helmet'

const Modules = () => {
    const { modules, refreshModules } = React.useContext(ManagementContext)
    const { value: query, clearValue: handleClear, onChange: handleChange } = useInputValue()
    const { createSortHandler, handleChangePage, handleChangeRowsPerPage, order, orderBy, page, rows, rowsPerPage } = useTableModel()
    const location = useLocation()
    const history = useHistory()
    const [selected, setSelected] = React.useState<any | undefined>(undefined)
    const [actionsAnchorEl, setActionsAnchorEl] = React.useState<null | HTMLElement>(null)
    const [deleteId, setDeleteId] = React.useState<number>(-1)
    const [loading, setLoading] = React.useState<boolean>(false)
    const [error, setError] = React.useState<boolean>(false)
    const [messageConfig, setMessageConfig] = React.useState<IMessageConfig>({ open: false, message: "", severity: "info" })
    const getModel = React.useCallback((): any[] => {
        const src = [...modules]
        if (Boolean(query)) {
            return src.filter((s: any) => {
                return s.name.toUpperCase().includes(query.toUpperCase()) || s.description.toUpperCase().includes(query.toUpperCase())
            })
        }
        return src
    }, [modules, query])
    const handleEdit = () => {
        setActionsAnchorEl(null)
        history.push({
            state: {
                edit: true,
                values: selected
            },
            pathname: `${AUTHENTICATION_PATH}${MODULE_PATH}${EDIT_PATH}`
        })
    }
    const handleConfirmDelete = (id: number) => {
        setActionsAnchorEl(null)
        setDeleteId(id)
    }
    const handleDelete = async () => {
        try {
            setLoading(true)
            await deleteModuleService(deleteId)
            setMessageConfig({ open: true, message: "Modulo eliminado!", severity: "success" })
            await refreshModules()
            setLoading(false)
            return true
        } catch (error) {
            setMessageConfig({ open: true, message: "No se pudo eliminar el modulo...!", severity: "error" })
            setLoading(false)
            return false
        }
    }
    const handleManageViews = (module: IModule) => {
        history.push({
            pathname: `${AUTHENTICATION_PATH}${MODULE_VIEW_PATH}`,
            state: { module }
        })
    }
    React.useEffect(() => {
        const initScreen = async () => {
            try {
                setLoading(true)
                await refreshModules()
                setLoading(false)
            } catch (error) {
                setLoading(false)
                setError(true)
            }
        }
        initScreen()
    }, [refreshModules])
    return (
        <Paper className="flex flex-col h-full overflow-hidden p-4 pb-0">
            <Helmet>
                <title>{`${ROOT_TITLE} - ${MODULE_TITLE}`}</title>
            </Helmet>
            <div className="flex items-center justify-between">
                <div className="flex items-center">
                    <Search
                        onChange={handleChange}
                        query={query}
                        onClear={handleClear}
                        placeholer={"Buscar por nombre o descripción..."}
                    />
                </div>
                <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    disableElevation
                    onClick={() => history.push(`${AUTHENTICATION_PATH}${MODULE_PATH}${NEW_PATH}`)}
                >
                    <Icon fontSize="small" style={{ marginRight: 5 }}>add</Icon>
                    {"Nuevo"}
                </Button>
                <FormDialog
                    open={Boolean(location.pathname === `${AUTHENTICATION_PATH}${MODULE_PATH}${NEW_PATH}` || location.pathname === `${AUTHENTICATION_PATH}${MODULE_PATH}${EDIT_PATH}`)}
                    onClose={() => history.push(`${AUTHENTICATION_PATH}${MODULE_PATH}`)}
                >
                    <ModuleForm
                        onSuccess={() => { setMessageConfig({ open: true, message: Boolean(selected) ? "Modulo actualizado!" : "Modulo registrado!", severity: "success" }); refreshModules(); if (Boolean(selected)) { setSelected(undefined) } }}
                        onError={() => setMessageConfig({ open: true, message: "No se pude registrar el modulo", severity: "error" })}
                        onClose={() => history.push(`${AUTHENTICATION_PATH}${MODULE_PATH}`)}
                    />
                </FormDialog>
            </div>
            <div className="flex h-full flex-col mt-4 overflow-hidden">
                <div className="flex flex-grow overflow-auto">
                    <TableContainer>
                        <Table stickyHeader size="small">
                            <TableHead>
                                <TableRow>
                                    {MODULE_TABLE_COLUMNS.map((headCell) => (
                                        <TableCell
                                            key={headCell.id}
                                            align={'left'}
                                            padding={"none"}
                                            sortDirection={orderBy === headCell.id ? order : false}
                                        >
                                            <TableSortLabel
                                                active={orderBy === headCell.id}
                                                direction={orderBy === headCell.id ? order : 'asc'}
                                                onClick={createSortHandler(headCell.id as any)}
                                            >
                                                {headCell.label}
                                                {orderBy === headCell.id ? (
                                                    <span className="hidden">
                                                        {order === 'desc' ? 'sorted descending' : 'sorted ascending'}
                                                    </span>
                                                ) : null}
                                            </TableSortLabel>
                                        </TableCell>
                                    ))}
                                    <TableCell
                                        padding={"default"}
                                    />
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {(rows(getModel()) as IModule[]).map((row, index) => {
                                    return (
                                        <TableRow
                                            hover
                                            tabIndex={-1}
                                            key={row.moduleId.toString()}
                                        >
                                            <TableCell component="th" scope="row" padding="none">{row.name}</TableCell>
                                            <TableCell padding="none" align="left">{row.description}</TableCell>
                                            <TableCell padding="none" align="left"><Icon style={{ color: "#a1a1a1" }}>{row.icon}</Icon></TableCell>
                                            <TableCell padding="none" align="left">{row.relativePath}</TableCell>
                                            <TableCell align="left">
                                                <IconButton onClick={(e) => { setActionsAnchorEl(e.currentTarget); setSelected(row) }} size="small">
                                                    <Icon fontSize="small">more_vert</Icon>
                                                </IconButton>
                                            </TableCell>
                                        </TableRow>
                                    )
                                })}
                                <Menu
                                    anchorEl={actionsAnchorEl}
                                    open={Boolean(actionsAnchorEl)}
                                    onClose={() => setActionsAnchorEl(null)}
                                >
                                    <MenuItem onClick={handleEdit} dense>{"Editar"}</MenuItem>
                                    <MenuItem onClick={() => handleConfirmDelete(selected?.moduleId ?? -1)} dense>{"Eliminar"}</MenuItem>
                                    <MenuItem onClick={() => handleManageViews(selected)} dense>{"Administrar vistas"}</MenuItem>
                                </Menu>
                            </TableBody>
                        </Table>
                    </TableContainer>
                </div>
                <div>
                    <TablePagination
                        rowsPerPageOptions={SMALL_ROWS_PER_PAGINATION_OPTIONS}
                        component="div"
                        count={getModel().length}
                        rowsPerPage={rowsPerPage}
                        page={page}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        labelRowsPerPage="Modulos por página"
                        labelDisplayedRows={LabelDisplayedRows}
                    />
                </div>
            </div>
            <ConfirmDialog
                onCancel={() => setDeleteId(-1)}
                onConfirm={handleDelete}
                open={Boolean(deleteId !== -1)}
                title={"¿Estás seguro que deseas eliminar?"}
                loading={loading}
            />
            <Snackbar open={messageConfig.open} autoHideDuration={6000} onClose={() => setMessageConfig({ ...messageConfig, open: false })} anchorOrigin={{ horizontal: "right", vertical: "bottom" }}>
                <Alert variant="filled" onClose={() => setMessageConfig({ ...messageConfig, open: false })} severity={messageConfig.severity}>
                    {messageConfig.message}
                </Alert>
            </Snackbar>
            <FixedLoadingIndicator loading={loading && Boolean(deleteId === -1)} />
            <ServerErrorHandler
                error={error}
                onSuccess={() => setError(false)}
                tryAgain={refreshModules}
            />
        </Paper>
    )
}


export default Modules
